import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

// Token storage helpers
function getToken() {
  return localStorage.getItem("bt_token");
}
function setToken(token) {
  if (token) localStorage.setItem("bt_token", token);
  else localStorage.removeItem("bt_token");
}

// Set up axios interceptor to attach token
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data);
    } catch (err) {
      console.error("Auth check:", err?.response?.status !== 401 ? err : "Not authenticated");
      if (err?.response?.status === 401) setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Heartbeat — sends every 30s while user is authenticated
  useEffect(() => {
    if (!user) {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      return;
    }

    const sendHeartbeat = async () => {
      try {
        await axios.post(`${API}/api/heartbeat`);
      } catch (err) {
        if (err.response?.status === 401) {
          setToken(null);
          setUser(false);
        }
      }
    };

    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 30000);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [user]);

  const sendOtp = async (phone) => {
    const { data } = await axios.post(`${API}/api/auth/send-otp`, { phone });
    // If whitelisted, token comes back in body
    if (data.token) {
      setToken(data.token);
    }
    return data;
  };

  const login = async (phone, otp) => {
    const { data } = await axios.post(`${API}/api/auth/verify-otp`, { phone, otp });
    if (data.token) {
      setToken(data.token);
    }
    // Fetch full user profile
    try {
      const { data: me } = await axios.get(`${API}/api/auth/me`);
      setUser(me);
    } catch (err) {
      console.error("Post-login me fetch:", err);
      setUser({ phone: data.phone, authenticated: true });
    }
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Logout error:", err);
    }
    setToken(null);
    sessionStorage.removeItem("bt_disclaimer_seen");
    setUser(false);
  };

  const trackEvent = async (eventType, path, referrer) => {
    try {
      await axios.post(`${API}/api/analytics/track`, {
        event_type: eventType,
        path,
        referrer,
      });
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Track error:", err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, sendOtp, logout, checkAuth, trackEvent }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
